/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Out-of-plane (improper) term that keeps a 3-coordinate sp² center coplanar
 * with its three neighbors — carbonyls, alkene termini, sp² nitrogen, aromatic
 * carbons. Proper torsions only constrain planarity when a through-bond 4-atom
 * chain exists (e.g. inside an aromatic ring); a TERMINAL sp² group (e.g. the
 * carbonyl C of formaldehyde, an amide) has no such chain, so nothing else
 * stops it pyramidalizing. This term supplies that.
 *
 * Functional form — harmonic in the signed perpendicular distance h of the
 * central atom from the plane through its three neighbors:
 *
 *   V = ½ · strength · h²,   h = (c − n1) · n̂,   n̂ = (n2−n1)×(n3−n1) normalized
 *
 * so `strength` is in eV/Å² and the minimum is h = 0 (planar) regardless of the
 * in-plane arrangement. No equilibrium offset is needed: planar is the single
 * universal target, and a well-built sp² group already sits there (V ≈ 0 at
 * rest), so there is no Regime-A/B dilemma — a pyramidal as-built geometry just
 * gets gently flattened, which is the desired behavior.
 *
 * Forces are −∇V by central-difference numerical gradient, the same pattern as
 * AngularBond / TorsionalBond, which GUARANTEES force = −∇V at every
 * configuration (no analytic-gradient bug can leak energy into NVE). The
 * acceleration scaling (GF_CONVERSION_CONSTANT / mass) matches the other bonded
 * terms.
 */

import { Atom } from './Atom.ts';
import { GF_CONVERSION_CONSTANT } from './physicalConstants.ts';

export class ImproperBond {
  static readonly DEFAULT_STRENGTH = 30;

  atom1: Atom; // central sp² atom
  atom2: Atom; // neighbor 1 (defines the plane)
  atom3: Atom; // neighbor 2
  atom4: Atom; // neighbor 3
  strength: number; // eV/Å²

  constructor(center: Atom, n1: Atom, n2: Atom, n3: Atom) {
    this.atom1 = center;
    this.atom2 = n1;
    this.atom3 = n2;
    this.atom4 = n3;
    this.strength = ImproperBond.DEFAULT_STRENGTH;
  }

  containsAtom(atom: Atom): boolean {
    return this.atom1 === atom || this.atom2 === atom || this.atom3 === atom || this.atom4 === atom;
  }

  /** Signed out-of-plane distance of the central atom from its neighbors' plane. */
  getOutOfPlaneDistance(): number {
    const c = this.atom1.position;
    const n1 = this.atom2.position;
    const n2 = this.atom3.position;
    const n3 = this.atom4.position;
    const e1x = n2.x - n1.x;
    const e1y = n2.y - n1.y;
    const e1z = n2.z - n1.z;
    const e2x = n3.x - n1.x;
    const e2y = n3.y - n1.y;
    const e2z = n3.z - n1.z;
    const mx = e1y * e2z - e1z * e2y;
    const my = e1z * e2x - e1x * e2z;
    const mz = e1x * e2y - e1y * e2x;
    const norm = Math.hypot(mx, my, mz);
    if (norm < 1e-9) return 0;
    return ((c.x - n1.x) * mx + (c.y - n1.y) * my + (c.z - n1.z) * mz) / norm;
  }

  /** Energy-only V = ½·strength·h². Used by compute() for the FD gradient. */
  private computeEnergy(): number {
    const h = this.getOutOfPlaneDistance();
    return 0.5 * this.strength * h * h;
  }

  /**
   * Compute out-of-plane force + energy on all four atoms via central-difference
   * numerical gradient (6 V-evals × 4 atoms × 3 axes). The energy is smooth in
   * all coordinates (away from collinear neighbors, where it returns 0), so the
   * FD gradient is well-defined everywhere.
   */
  compute(): number {
    if (this.strength === 0) return 0;
    if (this.atom1.fixed && this.atom2.fixed && this.atom3.fixed && this.atom4.fixed) return 0;
    if (!this.atom1.force || !this.atom2.force || !this.atom3.force || !this.atom4.force) return 0;

    const energy = this.computeEnergy();

    const hh = 1e-5;
    const atoms = [this.atom1, this.atom2, this.atom3, this.atom4];
    const forces = [this.atom1.force, this.atom2.force, this.atom3.force, this.atom4.force];
    const axes = ['x', 'y', 'z'] as const;

    for (let i = 0; i < 4; i++) {
      if (atoms[i].fixed) continue;
      const invMass = GF_CONVERSION_CONSTANT / atoms[i].mass;
      for (const axis of axes) {
        const p = atoms[i].position;
        const original = p[axis];
        p[axis] = original + hh;
        const vPlus = this.computeEnergy();
        p[axis] = original - hh;
        const vMinus = this.computeEnergy();
        p[axis] = original;
        forces[i][axis] += (-(vPlus - vMinus) / (2 * hh)) * invMass;
      }
    }

    return energy;
  }
}
